import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ClipboardList,
  ListChecks,
  BookOpen,
  Scale,
  PenLine,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Search,
  FileText,
} from "lucide-react";
import {
  ARTICLE_CITATION_OPTIONS,
  citationRuleFor,
  useCitationStyle,
} from "@/lib/use-citation-style";
import { NonReservedNotice } from "@/components/site/NonReservedNotice";



export const Route = createFileRoute("/education/legal-article-guide")({
  head: () => ({
    meta: [
      {
        title:
          "Legal Article Writing Guide — Structure, Analysis & Submission Checklist | Nkadimeng Danny",
      },
      {
        name: "description",
        content:
          "The standing guidelines our Legal Writing & Research tools follow: Task 1 requirements, article structure, analysis vs description, effective use of case law, editing stages, conclusion craft, quality submission checklist and common mistakes to avoid.",
      },
      { property: "og:title", content: "Legal Article Writing Guide" },
      {
        property: "og:description",
        content:
          "Requirements, structure, analysis, editing, conclusion and a quality submission checklist for legal articles.",
      },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://nkadimengdannylcs.com/education/legal-article-guide",
      },
    ],
  }),
  component: LegalArticleGuide,
});

function LegalArticleGuide() {
  const { style, setStyle, hydrated } = useCitationStyle();
  const rule = citationRuleFor(style);
  const styleLabel =
    ARTICLE_CITATION_OPTIONS.find((o) => o.slug === style)?.label ?? "OSCOLA";
  const styleShort =
    ARTICLE_CITATION_OPTIONS.find((o) => o.slug === style)?.short ?? "OSCOLA";

  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-14 md:py-20">
          <span className="gold-rule" />
          <p className="text-xs uppercase tracking-[0.25em] text-gold">
            Education · Standing Guidelines
          </p>
          <h1 className="mt-2 font-display text-4xl text-navy md:text-5xl">
            Legal Article Writing Guide
          </h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            These are the guidelines every Legal Writing and Research tool on
            this site follows — from Task 1 requirements to the final quality
            checklist. Use them as your own working brief.
          </p>
          <div className="mt-6 max-w-3xl">
            <NonReservedNotice />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/education/writing/article-editor"
              className="inline-flex items-center gap-2 rounded-md bg-gold px-5 py-3 text-sm font-medium text-navy hover:bg-gold/90"
            >
              <FileText className="size-4" /> Draft & download PDF
            </Link>
            <Link
              to="/education/research/worksheet"
              className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-3 text-sm font-medium text-white hover:bg-navy/90"
            >
              <ClipboardList className="size-4" /> Research worksheet
            </Link>
            <Link
              to="/education/writing"
              className="inline-flex items-center gap-2 rounded-md border border-input px-5 py-3 text-sm font-medium text-navy hover:bg-accent"
            >
              <PenLine className="size-4" /> Writing Academy
            </Link>
            <Link
              to="/education/research"
              className="inline-flex items-center gap-2 rounded-md border border-input px-5 py-3 text-sm font-medium text-navy hover:bg-accent"
            >
              <BookOpen className="size-4" /> Research Hub
            </Link>
          </div>

          {/* Citation style selector */}
          <div className="mt-6 rounded-lg border border-border bg-card p-4 md:p-5">
            <label
              htmlFor="citation-style-select"
              className="block text-xs font-semibold uppercase tracking-widest text-navy"
            >
              Your citation style
            </label>
            <p className="mt-1 text-xs text-muted-foreground">
              Pick once — the article editor, worksheet export, and the
              submission checklist below all enforce this choice.
            </p>
            <select
              id="citation-style-select"
              value={hydrated ? style : "oscola"}
              onChange={(e) => setStyle(e.target.value as never)}
              className="mt-2 w-full max-w-xl rounded-md border border-input bg-background p-2 text-sm"
            >
              {ARTICLE_CITATION_OPTIONS.map((opt) => (
                <option key={opt.slug} value={opt.slug}>
                  {opt.label} — {opt.note}
                </option>
              ))}
            </select>
            <div className="mt-3 grid gap-2 text-xs text-muted-foreground md:grid-cols-3">
              <p>
                <strong className="text-navy">Rule:</strong> {rule.keyRule}
              </p>
              <p>
                <strong className="text-navy">Placement:</strong>{" "}
                {rule.placement}
              </p>
              <p>
                <strong className="text-navy">Bibliography:</strong>{" "}
                {rule.bibliographyLabel}
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Task 1 Requirements */}
      <Section
        icon={ClipboardList}
        kicker="Task 1"
        title="Assignment requirements"
        subtitle="What to submit and the non-negotiables"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Card title="What to submit">
            <p>One legal article on a topic of your choice.</p>
          </Card>
          <Card title="Requirements">
            <ul className="ml-5 list-disc space-y-1">
              <li>
                <strong>Word limit:</strong> 2,000–3,000 words (excluding
                references, footnotes, bibliography).
              </li>
              <li>
                <strong>Topic:</strong> any legal issue (domestic or
                international).
              </li>
              <li>
                <strong>Formatting:</strong> Times New Roman, 12pt, 1.5 line
                spacing, justified.
              </li>
              <li>
                <strong>Citation:</strong> OSCOLA only — applied consistently
                throughout the submission. Your style:{" "}
                <strong className="text-navy">{styleShort}</strong>.
              </li>

              <li>
                <strong>Plagiarism:</strong> must be below 15%.
              </li>
              <li className="text-red-700">
                <strong>Deadline:</strong> 21 July 2026, 11:59 PM IST.
              </li>
            </ul>
          </Card>
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          This task is essential for internship certificate completion and
          publication eligibility.
        </p>
      </Section>

      {/* Structure */}
      <Section
        icon={ListChecks}
        kicker="Structure"
        title="Structure of a legal article"
        subtitle="The five parts every article needs"
        alt
      >
        <ol className="space-y-4">
          <Step n="1" title="Introduction — capturing reader attention">
            Opening hook (compelling fact, recent development, or thought-
            provoking question); background context for readers unfamiliar with
            the issue; a clear research question or thesis; scope definition
            (what you will and will not cover); a short structure preview.
          </Step>
          <Step n="2" title="Body — legal framework & argument">
            Set out the applicable law (constitution, legislation, common law,
            international instruments). Then argument 1, argument 2 and a
            counter-argument — each with a topic sentence, authority,
            application, and a mini-conclusion that ties back to the thesis.
          </Step>
          <Step n="3" title="Analysis — not description">
            Move beyond restating what a court said. Compare and contrast
            approaches, identify inconsistencies and gaps, question underlying
            assumptions, evaluate effectiveness and implications, propose
            alternatives with justification.
          </Step>
          <Step n="4" title="Conclusion — ending with impact">
            Summarise key findings, restate main arguments, suggest reforms or
            recommendations. Do not introduce new arguments, new citations, or
            apologies for limitations.
          </Step>
          <Step n="5" title="References">
            Complete bibliography of all sources cited, alphabetised and
            formatted in the chosen style.
          </Step>
        </ol>
      </Section>

      {/* Analysis vs Description */}
      <Section
        icon={Scale}
        kicker="Craft"
        title="Analysis vs description"
        subtitle="The critical distinction"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Card title="Descriptive (avoid)" tone="warn">
            <p className="italic">
              "The Supreme Court held that privacy is a fundamental right. The
              Court relied on international conventions."
            </p>
          </Card>
          <Card title="Analytical (aim for)" tone="good">
            <p className="italic">
              "While the Court's recognition of privacy as fundamental
              represents progress, the absence of a comprehensive legislative
              framework creates enforcement challenges. The reliance on
              international conventions, though innovative, raises questions
              about the hierarchy of legal sources and the extent of judicial
              lawmaking power."
            </p>
          </Card>
        </div>
        <div className="mt-6 rounded-lg border border-border bg-card p-5">
          <p className="font-semibold text-navy">Using case law effectively</p>
          <div className="mt-3 grid gap-3 md:grid-cols-2 text-sm">
            <div>
              <p className="text-xs uppercase tracking-widest text-red-700">
                Ineffective
              </p>
              <p className="mt-1 italic">
                "In Case X, the court held that contracts require
                consideration."
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-green-700">
                Effective
              </p>
              <p className="mt-1 italic">
                "The court's decision in Case X established the consideration
                requirement, but its application to digital contracts remains
                contentious. Critics argue traditional contract principles
                inadequately address click-wrap agreements, where 'acceptance'
                occurs without meaningful consent — suggesting a need for
                legislative intervention."
              </p>
            </div>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            When discussing cases: brief relevant facts only, legal issue and
            court's reasoning, significance and broader implications,
            criticisms or limitations, and how it supports <em>your</em>{" "}
            argument. Quality over quantity — analyse fewer cases deeply.
          </p>
        </div>
      </Section>

      {/* Standard Framework — Structure of a Legal Article */}
      <Section
        icon={FileText}
        kicker="Standard framework"
        title="Structure of a legal article"
        subtitle="The five-part template every article follows"
      >
        <div className="grid gap-3 md:grid-cols-2">
          <Card title="1. Title">
            Clear, concise, and descriptive of your content.
          </Card>
          <Card title="2. Introduction">
            Hook the reader with a compelling opening; provide background and
            context; state your thesis or research question; outline the
            structure of your article.
          </Card>
          <Card title="3. Main body">
            <ul className="ml-5 list-disc space-y-1">
              <li>Organised sections with clear headings</li>
              <li>Legal framework and current law</li>
              <li>Case-law analysis</li>
              <li>Critical evaluation and your analysis</li>
              <li>Comparative perspectives (if relevant)</li>
            </ul>
          </Card>
          <Card title="4. Conclusion">
            Summarise key findings, restate main arguments, suggest
            recommendations or reforms.
          </Card>
          <Card title="5. References">
            Complete bibliography of all sources cited, in{" "}
            <strong className="text-navy">{styleShort}</strong> format.
          </Card>
        </div>
      </Section>

      {/* Six Essential Research Steps */}
      <Section
        icon={ListChecks}
        kicker="Research method"
        title="Six essential steps"
        subtitle="Do these before you draft"
        alt
      >
        <ol className="grid gap-3 md:grid-cols-2">
          <Step n="1" title="Define your topic">
            Narrow your focus to a manageable, specific research question.
          </Step>
          <Step n="2" title="Background research">
            Start with secondary sources (textbooks, journal articles) to
            understand context.
          </Step>
          <Step n="3" title="Identify keywords">
            Create a list of relevant legal terms in your jurisdiction.
          </Step>
          <Step n="4" title="Search systematically">
            Use multiple databases and document sources immediately.
          </Step>
          <Step n="5" title="Evaluate credibility">
            Assess authority, accuracy, currency and relevance.
          </Step>
          <Step n="6" title="Organise your findings">
            Create a logical structure before you begin writing.
          </Step>
        </ol>
        <div className="mt-4">
          <Link
            to="/education/research/worksheet"
            className="inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-medium text-white hover:bg-navy/90"
          >
            <ClipboardList className="size-4" /> Open the guided worksheet{" "}
            <ArrowRight className="size-4" />
          </Link>
        </div>
      </Section>

      {/* Effective Search Strategies */}
      <Section
        icon={Search}
        kicker="Finding what you need"
        title="Effective search strategies"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Card title="Boolean operators">
            <ul className="ml-5 list-disc space-y-1">
              <li>
                <strong>AND:</strong> <code>privacy AND technology</code> —
                both terms required
              </li>
              <li>
                <strong>OR:</strong> <code>contract OR agreement</code> —
                either term
              </li>
              <li>
                <strong>NOT:</strong> <code>copyright NOT trademark</code> —
                exclude
              </li>
            </ul>
          </Card>
          <Card title="Advanced techniques">
            <ul className="ml-5 list-disc space-y-1">
              <li>
                Quotation marks: <code>"human rights"</code> (exact phrase)
              </li>
              <li>
                Wildcards: <code>legislat*</code> matches legislation,
                legislative, legislator
              </li>
              <li>Filter by date, jurisdiction, court level</li>
              <li>Search by citation or case name</li>
            </ul>
          </Card>
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          <strong className="text-navy">Pro tip:</strong> start broad, then
          narrow your search with specific terms.
        </p>
      </Section>

      {/* Editing */}

      <Section
        icon={PenLine}
        kicker="Polish"
        title="The editing process"
        subtitle="Three passes before submission"
        alt
      >
        <div className="grid gap-4 md:grid-cols-3">
          <Card title="Stage 1 — Content review">
            <ul className="ml-5 list-disc space-y-1">
              <li>Are arguments clear and logical?</li>
              <li>Is evidence sufficient and well-analysed?</li>
              <li>Are counter-arguments addressed?</li>
              <li>Does structure flow logically?</li>
            </ul>
          </Card>
          <Card title="Stage 2 — Technical review">
            <ul className="ml-5 list-disc space-y-1">
              <li>Are all citations complete and correct?</li>
              <li>Is formatting consistent throughout?</li>
              <li>Are headings properly structured?</li>
              <li>Is word count within limit?</li>
            </ul>
          </Card>
          <Card title="Stage 3 — Language review">
            <ul className="ml-5 list-disc space-y-1">
              <li>Grammar and spelling errors</li>
              <li>Sentence clarity and variety</li>
              <li>Professional tone maintained</li>
              <li>Remove redundancy</li>
            </ul>
          </Card>
        </div>
      </Section>

      {/* Quality Submission Checklist */}
      <Section
        icon={CheckCircle2}
        kicker="Before you submit"
        title="Quality submission checklist"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Card title="Content quality">
            <ul className="ml-5 list-disc space-y-1">
              <li>Clear thesis statement present</li>
              <li>Strong analytical content (not just descriptive)</li>
              <li>All claims supported with evidence</li>
              <li>Counter-arguments acknowledged</li>
              <li>Logical flow throughout</li>
            </ul>
          </Card>
          <Card title="Technical requirements">
            <ul className="ml-5 list-disc space-y-1">
              <li>Word count within brief (excluding references)</li>
              <li>Font: Times New Roman, 12pt</li>
              <li>Line spacing: 1.5, justified</li>
              <li>Format: Microsoft Word (.doc or .docx)</li>
              <li>
                Citation style: <strong className="text-navy">{styleLabel}</strong>{" "}
                — used consistently. {rule.placement}
              </li>
              <li>{rule.bibliographyLabel}</li>

              <li>Plagiarism: below 15%</li>
            </ul>
          </Card>
        </div>
        <div className="mt-4 rounded-lg border border-border bg-card p-5">
          <p className="font-semibold text-navy">Final checks</p>
          <ul className="mt-2 ml-5 list-disc space-y-1 text-sm">
            <li>All sources properly cited</li>
            <li>No grammatical or spelling errors</li>
            <li>References complete and formatted correctly</li>
          </ul>
        </div>
      </Section>

      {/* Common Mistakes */}
      <Section
        icon={AlertTriangle}
        kicker="Guardrails"
        title="Common mistakes to avoid"
        subtitle="Learn from others' errors"
        alt
      >
        <div className="grid gap-4 md:grid-cols-3">
          <Card title="Research errors" tone="warn">
            <ul className="ml-5 list-disc space-y-1">
              <li>Relying on unreliable sources (blogs, Wikipedia)</li>
              <li>Using outdated legal information</li>
              <li>Insufficient depth of research</li>
              <li>Not verifying information across sources</li>
            </ul>
          </Card>
          <Card title="Writing errors" tone="warn">
            <ul className="ml-5 list-disc space-y-1">
              <li>Purely descriptive content without analysis</li>
              <li>Poor organisation and structure</li>
              <li>Inconsistent citation style</li>
              <li>Missing or incomplete references</li>
            </ul>
          </Card>
          <Card title="Critical errors" tone="warn">
            <ul className="ml-5 list-disc space-y-1">
              <li>Plagiarism (even unintentional)</li>
              <li>Unsupported claims and assertions</li>
              <li>Ignoring counter-arguments</li>
              <li>Exceeding or falling short of the word limit</li>
              <li>Missing the submission deadline</li>
            </ul>
          </Card>
        </div>
      </Section>

      <section className="container-narrow pb-16">
        <div className="rounded-lg border border-border bg-card p-6">
          <p className="font-display text-xl text-navy">
            Ready to apply the guide?
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Use our AI writing and research tools — they enforce these rules
            (citation style, analysis over description, verification of
            authority) and flag any authority you must confirm at source.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to="/education/writing/tools"
              className="inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-medium text-white hover:bg-navy/90"
            >
              Citation & footnote tools <ArrowRight className="size-4" />
            </Link>
            <Link
              to="/education/research/irac"
              className="inline-flex items-center gap-2 rounded-md border border-input px-4 py-2 text-sm font-medium text-navy hover:bg-accent"
            >
              IRAC generator <ArrowRight className="size-4" />
            </Link>
            <Link
              to="/education/research/sources"
              className="inline-flex items-center gap-2 rounded-md border border-input px-4 py-2 text-sm font-medium text-navy hover:bg-accent"
            >
              Source verifier <ArrowRight className="size-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function Section({
  icon: Icon,
  kicker,
  title,
  subtitle,
  alt,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  kicker: string;
  title: string;
  subtitle?: string;
  alt?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      className={alt ? "border-t border-border bg-secondary/30" : "border-t border-border"}
    >
      <div className="container-narrow py-12 md:py-14">
        <div className="mb-6 flex items-start gap-3">
          <span className="rounded-md bg-navy/5 p-2 text-navy">
            <Icon className="size-5" />
          </span>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gold">
              {kicker}
            </p>
            <h2 className="font-display text-2xl text-navy md:text-3xl">
              {title}
            </h2>
            {subtitle ? (
              <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
            ) : null}
          </div>
        </div>
        {children}
      </div>
    </section>
  );
}

function Card({
  title,
  tone,
  children,
}: {
  title: string;
  tone?: "warn" | "good";
  children: React.ReactNode;
}) {
  const border =
    tone === "warn"
      ? "border-amber-200 bg-amber-50/60"
      : tone === "good"
        ? "border-green-200 bg-green-50/60"
        : "border-border bg-card";
  return (
    <div className={`rounded-lg border p-5 text-sm text-foreground/90 ${border}`}>
      <p className="mb-2 font-semibold text-navy">{title}</p>
      {children}
    </div>
  );
}

function Step({
  n,
  title,
  children,
}: {
  n: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <li className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-start gap-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-navy text-sm font-semibold text-white">
          {n}
        </span>
        <div>
          <p className="font-display text-lg text-navy">{title}</p>
          <p className="mt-1 text-sm text-muted-foreground">{children}</p>
        </div>
      </div>
    </li>
  );
}
